import React from "react";
import Link from "next/link";
import Script from 'next/script'
import { format } from "date-fns";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/cjs/styles/hljs";
import parse from "html-react-parser";
import { useRouter } from "next/router";
import DisqusComments from './DisqusComments';

function AuthorLink({ post }) {
  if (post.author_link) {
    return (
      <a href={post.author_link}
        className="hover:bg-primary transition-colors"
        target="_blank"
      >
        <span>{post.author_name}</span>
      </a>
    )
  }
  return <span>{post.author_name}</span>;
}

export const BlogPostContent = ({ data }) => {
  const router = useRouter();
  return (
    <div>
      <Script
        src="/js/vendor/highlight/highlight.min.js"
        strategy="afterInteractive"
        onLoad={() => {
          hljs.highlightAll();
        }}
      />
      <div className="responsive">
        <div className="flex text-xs uppercase my-3">
          <Link href={"/"} className="mr-3 hover:text-primary transition-colors">
            Home
          </Link>

          {data.postBy.categories && (
            <>
              <span className="mr-3">/</span>
              <Link
                href={"/category/blog/"}
                className="mr-3 hover:text-primary transition-colors">
                Blog
              </Link>
            </>
          )}
          <span className="mr-3">/</span>
          <span className="text-muted font-bold">{data.postBy.title}</span>
        </div>
      </div>
      <div className="bg-blog text-black">
        <h1 className="text-blog-title text-3xl font-bold mb-1 p-2">
          {data.postBy.title}
        </h1>
        {data.postBy.categories && (
          <div className="flex justify-between w-full flex-grow p-2">
            <div className="flex items-center">
              {data.postBy.categories.map((node) => {
                return (
                  <Link
                    href={node.uri}
                    key={node.uri}
                    className="mr-3 text-white bg-block-light hover:bg-primary transition-colors font-bold text-sm px-1"
                  >
                    {node.name}
                  </Link>
                );
              })}
              <span className="mr-3 mb-1 md:mb-0 text-sm text-black flex items-center">
                <svg
                  className="w-3 h-3 fill-current mr-1"
                  viewBox="-42 0 512 512.002"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M210.352 246.633c33.882 0 63.222-12.153 87.195-36.13 23.973-23.972 36.125-53.304 36.125-87.19 0-33.876-12.152-63.211-36.129-87.192C273.566 12.152 244.23 0 210.352 0c-33.887 0-63.22 12.152-87.192 36.125s-36.129 53.309-36.129 87.188c0 33.886 12.156 63.222 36.133 87.195 23.977 23.969 53.313 36.125 87.188 36.125zm0 0M426.129 393.703c-.692-9.976-2.09-20.86-4.149-32.351-2.078-11.579-4.753-22.524-7.957-32.528-3.308-10.34-7.808-20.55-13.37-30.336-5.774-10.156-12.555-19-20.165-26.277-7.957-7.613-17.699-13.734-28.965-18.2-11.226-4.44-23.668-6.69-36.976-6.69-5.227 0-10.281 2.144-20.043 8.5a2711.03 2711.03 0 01-20.879 13.46c-6.707 4.274-15.793 8.278-27.016 11.903-10.949 3.543-22.066 5.34-33.039 5.34-10.972 0-22.086-1.797-33.047-5.34-11.21-3.622-20.296-7.625-26.996-11.899-7.77-4.965-14.8-9.496-20.898-13.469-9.75-6.355-14.809-8.5-20.035-8.5-13.313 0-25.75 2.254-36.973 6.7-11.258 4.457-21.004 10.578-28.969 18.199-7.605 7.281-14.39 16.12-20.156 26.273-5.558 9.785-10.058 19.992-13.371 30.34-3.2 10.004-5.875 20.945-7.953 32.524-2.059 11.476-3.457 22.363-4.149 32.363A438.821 438.821 0 000 423.949c0 26.727 8.496 48.363 25.25 64.32 16.547 15.747 38.441 23.735 65.066 23.735h246.532c26.625 0 48.511-7.984 65.062-23.734 16.758-15.946 25.254-37.586 25.254-64.325-.004-10.316-.351-20.492-1.035-30.242zm0 0" />
                </svg>
                <AuthorLink post={data.postBy} />
              </span>
              <span className="mr-3 mb-1 md:mb-0 transition-colors text-sm text-black flex items-center">
                <svg
                  className="w-3 h-3 fill-current mr-1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 488 488"
                >
                  <path d="M177.854 269.311c0-6.115-4.96-11.069-11.08-11.069h-38.665c-6.113 0-11.074 4.954-11.074 11.069v38.66c0 6.123 4.961 11.079 11.074 11.079h38.665c6.12 0 11.08-4.956 11.08-11.079v-38.66zM274.483 269.311c0-6.115-4.961-11.069-11.069-11.069h-38.67c-6.113 0-11.074 4.954-11.074 11.069v38.66c0 6.123 4.961 11.079 11.074 11.079h38.67c6.108 0 11.069-4.956 11.069-11.079v-38.66zM371.117 269.311c0-6.115-4.961-11.069-11.074-11.069h-38.665c-6.12 0-11.08 4.954-11.08 11.069v38.66c0 6.123 4.96 11.079 11.08 11.079h38.665c6.113 0 11.074-4.956 11.074-11.079v-38.66zM177.854 365.95c0-6.125-4.96-11.075-11.08-11.075h-38.665c-6.113 0-11.074 4.95-11.074 11.075v38.653c0 6.119 4.961 11.074 11.074 11.074h38.665c6.12 0 11.08-4.956 11.08-11.074V365.95zM274.483 365.95c0-6.125-4.961-11.075-11.069-11.075h-38.67c-6.113 0-11.074 4.95-11.074 11.075v38.653c0 6.119 4.961 11.074 11.074 11.074h38.67c6.108 0 11.069-4.956 11.069-11.074V365.95zM371.117 365.95c0-6.125-4.961-11.075-11.069-11.075h-38.67c-6.12 0-11.08 4.95-11.08 11.075v38.653c0 6.119 4.96 11.074 11.08 11.074h38.67c6.108 0 11.069-4.956 11.069-11.074V365.95z" />
                  <path d="M440.254 54.354v59.05c0 26.69-21.652 48.198-48.338 48.198h-30.493c-26.688 0-48.627-21.508-48.627-48.198V54.142h-137.44v59.262c0 26.69-21.938 48.198-48.622 48.198H96.235c-26.685 0-48.336-21.508-48.336-48.198v-59.05c-23.323.703-42.488 20.002-42.488 43.723v346.061c0 24.167 19.588 44.015 43.755 44.015h389.82c24.131 0 43.755-19.889 43.755-44.015V98.077c0-23.721-19.164-43.02-42.487-43.723zm-14.163 368.234c0 10.444-8.468 18.917-18.916 18.917H80.144c-10.448 0-18.916-8.473-18.916-18.917V243.835c0-10.448 8.467-18.921 18.916-18.921h327.03c10.448 0 18.916 8.473 18.916 18.921l.001 178.753z" />
                  <path d="M96.128 129.945h30.162c9.155 0 16.578-7.412 16.578-16.567V16.573C142.868 7.417 135.445 0 126.29 0H96.128C86.972 0 79.55 7.417 79.55 16.573v96.805c0 9.155 7.422 16.567 16.578 16.567zM361.035 129.945h30.162c9.149 0 16.572-7.412 16.572-16.567V16.573C407.77 7.417 400.347 0 391.197 0h-30.162c-9.154 0-16.577 7.417-16.577 16.573v96.805c0 9.155 7.423 16.567 16.577 16.567z" />
                </svg>
                <span>
                  {format(new Date(data.postBy.post_date), "dd MMM, yyyy")}
                </span>
              </span>
            </div>
          </div>
        )}

        <div className="blog-content">
          {parse(data.postBy.post_content, {
            trim: true,
            replace: (domNode) => {
              if (
                domNode.attribs &&
                // the space at the end is important:
                domNode.attribs.class === "wp-block-syntaxhighlighter-code "
              ) {
                return (
                  <SyntaxHighlighter
                    language="csharp"
                    style={a11yDark}
                    showLineNumbers={true}
                  >
                    {domNode.children[0].children[0].data.trim()}
                  </SyntaxHighlighter>
                );
              }
            },
          })}
          <p>If you liked this content <a target="_blank" href="https://twitter.com/TheRadCade">follow us on Twitter</a> for more!</p>
        </div >
        <div>

          <div className="p-5">
            <DisqusComments post={data.postBy} />
          </div>
        </div>
      </div >
    </div >
  );
};
